import React, { type ReactElement, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export enum InputType {
  INPUT = "input",
  TEXTAREA = "textarea",
}

interface EditableWrapperProps {
  value: string;
  children: ReactElement;
  className?: string;
  onEdit: (newValue: string) => void;
  onEnter?: () => void;
  type?: InputType;
  disabled?: boolean;
  maxLength?: number;

  // If in linear mode, always editable and no outlines
  linearMode?: boolean;

  realTimeUpdate?: boolean;
}

const EditableWrapper: React.FC<EditableWrapperProps> = ({
  children,
  onEdit,
  onEnter,
  value,
  className = "",
  type = InputType.INPUT,
  disabled = false,
  linearMode = false,
  maxLength,
  realTimeUpdate = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  const handleBlur = () => {
    setIsEditing(false);
  };

  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if ((event.metaKey || event.ctrlKey) && event.key === "a") {
      event.stopPropagation();
      return;
    }

    if (event.key === "Enter" && !event.shiftKey) {
      if (onEnter) {
        event.preventDefault();
        onEnter();
      }
      handleBlur();
    }
  };

  const adjustTextAreaHeight = (target: HTMLTextAreaElement) => {
    target.style.height = "auto"; // Reset the height first
    target.style.height = `${target.scrollHeight}px`; // Set the height based on content
  };

  useEffect(() => {
    if (
      type === InputType.TEXTAREA &&
      inputRef.current &&
      inputRef.current instanceof HTMLTextAreaElement
    ) {
      adjustTextAreaHeight(inputRef.current);
    }
  }, [isEditing, type, tempValue]);

  const childClassName = React.Children.only(children).props.className;

  const handleClick = (
    e: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    e.stopPropagation();
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  if (isEditing || linearMode) {
    if (type === InputType.TEXTAREA) {
      return (
        <textarea
          ref={inputRef as React.MutableRefObject<HTMLTextAreaElement>}
          className={cn(
            "bg-transparent focus:border-none focus:outline-solid focus:outline-primary-0 w-full border-none p-0 resize-none outline-hidden outline-offset-0",
            linearMode &&
              "outline-hidden focus:outline-hidden outline-transparent focus:outline-transparent",
            className,
            childClassName,
          )}
          value={tempValue}
          onChange={(e) => {
            const { target } = e;
            adjustTextAreaHeight(target);
            setTempValue(target.value);
            onEdit(target.value);
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyPress}
          onMouseDown={handleClick}
          onClick={handleClick}
          rows={1}
          style={{
            height: `${(tempValue.match(/\n/g) || []).length * 20 + 20}px`,
          }}
          maxLength={maxLength}
        />
      );
    } else {
      return (
        <input
          ref={inputRef as React.MutableRefObject<HTMLInputElement>}
          className={cn(
            "bg-transparent w-full focus:border-none focus:outline-solid focus:outline-primary-0 border-none p-0 outline-hidden outline-offset-0",
            linearMode &&
              "outline-hidden focus:outline-hidden outline-transparent focus:outline-transparent",
            className,
            childClassName,
          )}
          value={tempValue}
          onChange={(e) => {
            setTempValue(e.target.value);
            onEdit(e.target.value);
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyPress}
          onMouseDown={handleClick}
          onClick={handleClick}
        />
      );
    }
  }

  // Wrap the child with a div that has the onClick handler.
  return (
    <div
      className={cn(
        "hover:outline-secondary-600 hover:outline-solid w-full",
        linearMode && "hover:outline-hidden hover:outline-transparent",
        className,
        childClassName,
      )}
      onClick={() => !disabled && setIsEditing(true)}
    >
      {children}
    </div>
  );
};

export default React.memo(EditableWrapper);
